import "prosemirror-view/style/prosemirror.css";
import "prosemirror-tables/style/tables.css";
import React, { JSX, useState, useCallback } from "react";
import { keymap } from "prosemirror-keymap";
import { baseKeymap, setBlockType, toggleMark } from "prosemirror-commands";
import { history, redo, undo } from "prosemirror-history";
import { Command, EditorState, Selection, TextSelection, Transaction } from "prosemirror-state";
import {
  DOMOutputSpec,
  DOMParser,
  DOMSerializer,
  Fragment,
  MarkType,
  Node,
  NodeType,
  Schema
} from "prosemirror-model";
import { liftListItem, sinkListItem, splitListItem, wrapInList } from "prosemirror-schema-list";
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  goToNextCell,
  isInTable,
  tableEditing,
  tableNodes
} from "prosemirror-tables";
import {
  Box,
  Button as MuiButton,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import TitleIcon from "@mui/icons-material/Title";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PostAddIcon from "@mui/icons-material/PostAdd";

import ProseMirror from "./ProseMirror";
import useProseMirror from "./useProsemirror";
import styles from "./style.module.css";
import insertTable from "./commands";

function getHeadingAttrs(level: number, dom: HTMLElement) {
  let align = dom.style.textAlign || dom.getAttribute("align") || null;
  if (!align) {
    const span = dom.querySelector("span[style*='text-align']");
    if (span instanceof HTMLElement && span.style.textAlign) {
      align = span.style.textAlign;
    }
  }
  return { level, align: align || null };
}

const myNodes = {
  doc: {
    content: "block+"
  },
  paragraph: {
    group: "block",
    content: "inline*",
    attrs: { align: { default: null } },
    toDOM(node: Node): DOMOutputSpec {
      return node.attrs.align ? ["p", { style: `text-align: ${node.attrs.align};` }, 0] : ["p", 0];
    },
    parseDOM: [
      {
        tag: "p",
        getAttrs(dom: HTMLElement) {
          let align = dom.style.textAlign || dom.getAttribute("align") || null;
          if (!align) {
            const span = dom.querySelector("span[style*='text-align']");
            if (span instanceof HTMLElement && span.style.textAlign) {
              align = span.style.textAlign;
            }
          }
          return { align: align || null };
        }
      }
    ]
  },
  heading: {
    attrs: { level: { default: 1 }, align: { default: null } },
    content: "inline*",
    group: "block",
    defining: true,
    parseDOM: [
      { tag: "h1", getAttrs: (dom: HTMLElement) => getHeadingAttrs(1, dom) },
      { tag: "h2", getAttrs: (dom: HTMLElement) => getHeadingAttrs(2, dom) },
      { tag: "h3", getAttrs: (dom: HTMLElement) => getHeadingAttrs(3, dom) },
      { tag: "h4", getAttrs: (dom: HTMLElement) => getHeadingAttrs(4, dom) },
      { tag: "h5", getAttrs: (dom: HTMLElement) => getHeadingAttrs(5, dom) },
      { tag: "h6", getAttrs: (dom: HTMLElement) => getHeadingAttrs(6, dom) }
    ],
    toDOM(node: Node): DOMOutputSpec {
      const attrs: Record<string, string> = {};
      if (node.attrs.align) {
        attrs.style = `text-align: ${node.attrs.align};`;
      }
      return [`h${node.attrs.level}`, attrs, 0];
    }
  },
  bullet_list: {
    content: "list_item+",
    group: "block",
    parseDOM: [{ tag: "ul" }],
    toDOM(): DOMOutputSpec {
      return ["ul", 0];
    }
  },
  ordered_list: {
    content: "list_item+",
    group: "block",
    attrs: { order: { default: 1 } },
    parseDOM: [
      {
        tag: "ol",
        getAttrs(dom: HTMLElement) {
          return {
            order: dom.hasAttribute("start") ? Number(dom.getAttribute("start")) : 1
          };
        }
      }
    ],
    toDOM(node: Node): DOMOutputSpec {
      return node.attrs.order === 1 ? ["ol", 0] : ["ol", { start: node.attrs.order }, 0];
    }
  },
  list_item: {
    content: "paragraph block*",
    group: "list_item",
    defining: true,
    parseDOM: [{ tag: "li" }],
    toDOM(): DOMOutputSpec {
      return ["li", 0];
    }
  },
  customKeyword: {
    selectable: true,
    draggable: false,
    attrs: { keyword: {} },
    inline: true,
    group: "inline",
    toDOM(node: Node): DOMOutputSpec {
      return [
        "span",
        { class: "custom-keyword", "data-keyword": node.attrs.keyword },
        `{{${node.attrs.keyword}}}`
      ];
    },
    parseDOM: [
      {
        tag: "span[data-keyword]",
        getAttrs(dom: HTMLElement) {
          return { keyword: dom.getAttribute("data-keyword") || "" };
        }
      },
      {
        tag: "span",
        getAttrs(dom: HTMLElement) {
          const text = dom.textContent || "";
          const match = text.trim().match(/^\{\{\s*([^}]+?)\s*\}\}$/);
          if (match && match[1]) {
            return { keyword: match[1].trim() };
          }
          return false;
        }
      }
    ]
  },
  text: {
    group: "inline"
  },
  ...tableNodes({
    tableGroup: "block",
    cellContent: "block+",
    cellAttributes: {}
  })
};

const myMarks = {
  bold: {
    toDOM(): DOMOutputSpec {
      return ["strong", 0];
    },
    parseDOM: [{ tag: "strong" }, { tag: "b" }]
  },
  italic: {
    toDOM(): DOMOutputSpec {
      return ["em", 0];
    },
    parseDOM: [{ tag: "em" }, { tag: "i" }]
  },
  underline: {
    toDOM(): DOMOutputSpec {
      return ["u", 0];
    },
    parseDOM: [{ tag: "u" }]
  },
  link: {
    attrs: { href: {} },
    toDOM(node: Node): DOMOutputSpec {
      return ["a", { href: node.attrs.href, target: "_blank", rel: "noopener noreferrer" }, 0];
    },
    parseDOM: [
      {
        tag: "a",
        getAttrs(dom: HTMLElement) {
          const href = dom.getAttribute("href");
          if (!href) return false;
          return { href };
        }
      }
    ],
    inclusive: false
  },
  alignLeft: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    },
    parseDOM: []
  },
  alignCenter: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    },
    parseDOM: []
  },
  alignRight: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    },
    parseDOM: []
  },
  alignJustify: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    },
    parseDOM: []
  },
  list: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    }
  },
  bulletList: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    }
  },
  orderedList: {
    toDOM(): DOMOutputSpec {
      return ["span", 0];
    }
  }
};

export const customSchema = new Schema({
  nodes: myNodes,
  marks: myMarks
});

export function isMarkActive(state: EditorState, mark: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  return empty
    ? !!mark.isInSet(state.storedMarks || $from.marks())
    : state.doc.rangeHasMark(from, to, mark);
}

export function toggleMarkCommand(mark: MarkType): Command {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean =>
    toggleMark(mark)(state, dispatch);
}

export const toggleBold = toggleMarkCommand(customSchema.marks.bold);
export const toggleItalic = toggleMarkCommand(customSchema.marks.italic);
export const toggleUnderline = toggleMarkCommand(customSchema.marks.underline);

export function isBold(state: EditorState): boolean {
  return isMarkActive(state, customSchema.marks.bold);
}

export function isItalic(state: EditorState): boolean {
  return isMarkActive(state, customSchema.marks.italic);
}

export function isUnderline(state: EditorState): boolean {
  return isMarkActive(state, customSchema.marks.underline);
}

export function isLink(state: EditorState): boolean {
  const { doc, selection } = state;
  const { from, to, empty, $from } = selection;
  if (!customSchema.marks.link) return false;
  if (empty) {
    return !!customSchema.marks.link.isInSet(state.storedMarks || $from.marks());
  }
  return doc.rangeHasMark(from, to, customSchema.marks.link);
}

export function getActiveLinkInfo(
  state: EditorState
): { href: string; text: string; from: number; to: number } | null {
  const { doc, selection } = state;
  const { from, to, empty, $from } = selection;
  const linkType = customSchema.marks.link;
  if (!linkType) return null;

  if (empty) {
    const mark = linkType.isInSet(state.storedMarks || $from.marks());
    if (!mark) return null;
    let linkStart = $from.pos;
    let linkEnd = $from.pos;
    let linkText = "";
    $from.parent.forEach((child, offset) => {
      const nodeStart = $from.start() + offset;
      const nodeEnd = nodeStart + child.nodeSize;
      if (child.isText && nodeStart <= $from.pos && $from.pos <= nodeEnd) {
        if (linkType.isInSet(child.marks)) {
          linkStart = nodeStart;
          linkEnd = nodeEnd;
          linkText = child.text || "";
        }
      }
    });
    return {
      href: (mark.attrs.href as string) || "",
      text: linkText,
      from: linkStart,
      to: linkEnd
    };
  }

  let foundHref = "";
  let linkStart = from;
  let linkEnd = to;
  doc.nodesBetween(from, to, (node, pos) => {
    const mark = linkType.isInSet(node.marks);
    if (mark) {
      foundHref = (mark.attrs.href as string) || "";
      linkStart = Math.min(linkStart, pos);
      linkEnd = Math.max(linkEnd, pos + node.nodeSize);
    }
  });

  if (foundHref) {
    const selectedText = doc.textBetween(linkStart, linkEnd);
    return { href: foundHref, text: selectedText, from: linkStart, to: linkEnd };
  }
  return null;
}

export function applyLink(
  state: EditorState,
  dispatch: (tr: Transaction) => void,
  href: string,
  linkText?: string,
  targetRange?: { from: number; to: number }
): void {
  const linkType = customSchema.marks.link;
  if (!linkType) return;

  let formattedHref = href.trim();
  if (
    formattedHref &&
    !/^https?:\/\//i.test(formattedHref) &&
    !/^mailto:/i.test(formattedHref) &&
    !/^tel:/i.test(formattedHref) &&
    !formattedHref.startsWith("/") &&
    !formattedHref.startsWith("#")
  ) {
    formattedHref = `https://${formattedHref}`;
  }

  const { from, to } = targetRange || state.selection;
  const isRangeEmpty = from === to;

  if (isRangeEmpty && linkText) {
    const textNode = customSchema.text(linkText, [linkType.create({ href: formattedHref })]);
    const tr = state.tr.replaceWith(from, to, textNode).scrollIntoView();
    dispatch(tr);
  } else if (!isRangeEmpty) {
    const tr = state.tr
      .removeMark(from, to, linkType)
      .addMark(from, to, linkType.create({ href: formattedHref }))
      .scrollIntoView();
    dispatch(tr);
  }
}

export function removeLink(
  state: EditorState,
  dispatch: (tr: Transaction) => void,
  targetRange?: { from: number; to: number }
): void {
  const linkType = customSchema.marks.link;
  if (!linkType) return;
  const { from, to } = targetRange || state.selection;
  if (from === to) return;
  const tr = state.tr.removeMark(from, to, linkType).scrollIntoView();
  dispatch(tr);
}

export function isListActive(state: EditorState, listType: NodeType): boolean {
  const { $from, $to } = state.selection;
  const range = $from.blockRange($to);
  if (!range) return false;
  for (let d = range.depth; d > 0; d -= 1) {
    if (range.$from.node(d).type === listType) {
      return true;
    }
  }
  return false;
}

export function isBulletListActive(state: EditorState): boolean {
  return isListActive(state, customSchema.nodes.bullet_list);
}

export function isOrderedListActive(state: EditorState): boolean {
  return isListActive(state, customSchema.nodes.ordered_list);
}

export function isList(state: EditorState): boolean {
  return isBulletListActive(state) || isOrderedListActive(state);
}

export function toggleListCommand(listType: NodeType): Command {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    const { $from, $to } = state.selection;
    const range = $from.blockRange($to);
    if (!range) return false;

    for (let d = range.depth; d > 0; d -= 1) {
      const node = range.$from.node(d);
      if (node.type === listType) {
        return liftListItem(customSchema.nodes.list_item)(state, dispatch);
      }
      if (
        node.type === customSchema.nodes.bullet_list ||
        node.type === customSchema.nodes.ordered_list
      ) {
        if (dispatch) {
          const pos = range.$from.before(d);
          const tr = state.tr.setNodeMarkup(pos, listType);
          dispatch(tr.scrollIntoView());
        }
        return true;
      }
    }
    return wrapInList(listType)(state, dispatch);
  };
}

export const toggleBulletedList = toggleListCommand(customSchema.nodes.bullet_list);
export const toggleOrderedList = toggleListCommand(customSchema.nodes.ordered_list);

export function getActiveAlignment(state: EditorState): "left" | "center" | "right" | "justify" {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    const node = $from.node(d);
    if (node.type === customSchema.nodes.paragraph || node.type === customSchema.nodes.heading) {
      const { align } = node.attrs;
      if (align === "center" || align === "right" || align === "justify") {
        return align;
      }
      return "left";
    }
  }
  return "left";
}

export function isAlignLeft(state: EditorState): boolean {
  return getActiveAlignment(state) === "left";
}

export function isAlignCenter(state: EditorState): boolean {
  return getActiveAlignment(state) === "center";
}

export function isAlignRight(state: EditorState): boolean {
  return getActiveAlignment(state) === "right";
}

export function isAlignJustify(state: EditorState): boolean {
  return getActiveAlignment(state) === "justify";
}

function computeNewAlignment(currentAlign: string, targetAlign: string): string | null {
  if (currentAlign === targetAlign && targetAlign !== "left") {
    return null;
  }
  if (targetAlign === "left") {
    return null;
  }
  return targetAlign;
}

export function setAlignmentCommand(alignment: "left" | "center" | "right" | "justify"): Command {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    const { from, to, $from } = state.selection;
    const { tr } = state;
    let applicable = false;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type === customSchema.nodes.paragraph || node.type === customSchema.nodes.heading) {
        applicable = true;
        const currentAlign = node.attrs.align || "left";
        const newAlign = computeNewAlignment(currentAlign, alignment);
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          align: newAlign
        });
      }
    });

    if (!applicable) {
      for (let d = $from.depth; d > 0; d -= 1) {
        const node = $from.node(d);
        if (
          node.type === customSchema.nodes.paragraph ||
          node.type === customSchema.nodes.heading
        ) {
          applicable = true;
          const pos = $from.before(d);
          const currentAlign = node.attrs.align || "left";
          const newAlign = computeNewAlignment(currentAlign, alignment);
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            align: newAlign
          });
          break;
        }
      }
    }

    if (applicable) {
      const alignMarks = [
        customSchema.marks.alignLeft,
        customSchema.marks.alignCenter,
        customSchema.marks.alignRight,
        customSchema.marks.alignJustify
      ].filter(Boolean);
      alignMarks.forEach((m) => {
        tr.removeMark(from, to, m);
      });

      if (dispatch) dispatch(tr.scrollIntoView());
      return true;
    }
    return false;
  };
}

export const toggleAlignLeft = setAlignmentCommand("left");
export const toggleAlignCenter = setAlignmentCommand("center");
export const toggleAlignRight = setAlignmentCommand("right");
export const toggleAlignJustify = setAlignmentCommand("justify");

export function getCurrentHeadingLevel(state: EditorState): number {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    const node = $from.node(d);
    if (node.type === customSchema.nodes.heading) {
      return (node.attrs.level as number) || 1;
    }
  }
  return 0;
}

export function setHeadingCommand(level: number): Command {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    const { $from, empty } = state.selection;
    if (level === 0) {
      if ($from.parent.type === customSchema.nodes.heading) {
        // If cursor is at the end of a heading that already has text,
        // create a new paragraph below the heading instead of converting it
        if (
          empty &&
          $from.parent.content.size > 0 &&
          $from.parentOffset === $from.parent.content.size
        ) {
          if (dispatch) {
            const insertPos = $from.after();
            const p = customSchema.nodes.paragraph.create();
            const tr = state.tr.insert(insertPos, p);
            tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 1)));
            dispatch(tr.scrollIntoView());
          }
          return true;
        }

        // If user has a partial selection inside the heading (not from the start)
        if (!empty && $from.parentOffset > 0) {
          if (dispatch) {
            const tr = state.tr.split($from.pos, 1, [{ type: customSchema.nodes.paragraph }]);
            dispatch(tr.scrollIntoView());
          }
          return true;
        }
      }
      return setBlockType(customSchema.nodes.paragraph)(state, dispatch);
    }

    const currentLevel = getCurrentHeadingLevel(state);
    if (currentLevel === level) {
      return setBlockType(customSchema.nodes.paragraph)(state, dispatch);
    }
    const currentAlign = $from.parent.attrs.align || null;
    return setBlockType(customSchema.nodes.heading, {
      level,
      align: currentAlign
    })(state, dispatch);
  };
}

export function isInsideTable(state: EditorState): boolean {
  try {
    return isInTable(state);
  } catch {
    return false;
  }
}

export function insertParagraphBeforeTable(
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    const node = $from.node(d);
    if (node.type === customSchema.nodes.table) {
      if (dispatch) {
        const beforeTable = $from.before(d);
        const p = customSchema.nodes.paragraph.createAndFill();
        if (p) {
          const tr = state.tr.insert(beforeTable, p);
          tr.setSelection(TextSelection.near(tr.doc.resolve(beforeTable + 1)));
          dispatch(tr.scrollIntoView());
        }
      }
      return true;
    }
  }
  return false;
}

export function insertParagraphAfterTable(
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    const node = $from.node(d);
    if (node.type === customSchema.nodes.table) {
      if (dispatch) {
        const afterTable = $from.after(d);
        const p = customSchema.nodes.paragraph.createAndFill();
        if (p) {
          const tr = state.tr.insert(afterTable, p);
          tr.setSelection(TextSelection.near(tr.doc.resolve(afterTable + 1)));
          dispatch(tr.scrollIntoView());
        }
      }
      return true;
    }
  }
  return false;
}

export function insertCustomKeyword(
  keyword: string,
  state: EditorState,
  dispatch: (tr: Transaction) => void
): boolean {
  const type = customSchema.nodes.customKeyword;
  const { $from } = state.selection;
  if (!$from.parent.canReplaceWith($from.index(), $from.index(), type)) return false;
  dispatch(state.tr.replaceSelectionWith(type.create({ keyword })));
  return true;
}

export const handleEnter: Command = (
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean => {
  // 1. If inside list item, split or lift list item
  if (splitListItem(customSchema.nodes.list_item)(state, dispatch)) {
    return true;
  }
  // 2. If at the end of a heading, create a new paragraph below the heading
  const { $from, empty } = state.selection;
  if (
    empty &&
    $from.parent.type === customSchema.nodes.heading &&
    $from.parent.content.size > 0 &&
    $from.parentOffset === $from.parent.content.size
  ) {
    if (dispatch) {
      const tr = state.tr.split($from.pos, 1, [{ type: customSchema.nodes.paragraph }]);
      dispatch(tr.scrollIntoView());
    }
    return true;
  }
  // 3. Fallback to baseKeymap Enter (splits blocks in paragraphs, table cells, etc.)
  if (baseKeymap.Enter) {
    return baseKeymap.Enter(state, dispatch);
  }
  return false;
};

function blockHasContent(node: Node | null | undefined): boolean {
  if (!node) return false;
  if (!node.isTextblock) return true;
  if (node.content.size === 0) return false;
  return node.textContent.trim().length > 0 || (node.childCount > 0 && !node.firstChild?.isText);
}

export const handleArrowUp: Command = (
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean => {
  const { empty, $from } = state.selection;
  if (!empty) return false;

  const atStart = Selection.atStart(state.doc);
  if ($from.pos <= atStart.from) {
    if (!blockHasContent($from.parent) || !blockHasContent(state.doc.firstChild)) {
      return false;
    }

    if (dispatch) {
      const p = customSchema.nodes.paragraph.create();
      const tr = state.tr.insert(0, p);
      tr.setSelection(TextSelection.create(tr.doc, 1));
      dispatch(tr.scrollIntoView());
    }
    return true;
  }

  if (baseKeymap.ArrowUp) {
    return baseKeymap.ArrowUp(state, dispatch);
  }
  return false;
};

export const handleArrowDown: Command = (
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean => {
  const { empty, $to } = state.selection;
  if (!empty) return false;

  const atEnd = Selection.atEnd(state.doc);
  if ($to.pos >= atEnd.to) {
    if (!blockHasContent($to.parent) || !blockHasContent(state.doc.lastChild)) {
      return false;
    }

    if (dispatch) {
      const p = customSchema.nodes.paragraph.create();
      const insertPos = state.doc.content.size;
      const tr = state.tr.insert(insertPos, p);
      tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
      dispatch(tr.scrollIntoView());
    }
    return true;
  }

  if (baseKeymap.ArrowDown) {
    return baseKeymap.ArrowDown(state, dispatch);
  }
  return false;
};

export const proseMirrorOptions: Parameters<typeof useProseMirror>[0] & { schema: Schema } = {
  schema: customSchema,
  plugins: [
    history(),
    tableEditing(),
    keymap({
      ...baseKeymap,
      "Mod-z": undo,
      "Mod-y": redo,
      "Mod-Shift-z": redo,
      "Mod-b": toggleBold,
      "Mod-i": toggleItalic,
      "Mod-u": toggleUnderline,
      Enter: handleEnter,
      ArrowUp: handleArrowUp,
      ArrowDown: handleArrowDown,
      "Mod-[": liftListItem(customSchema.nodes.list_item),
      "Mod-]": sinkListItem(customSchema.nodes.list_item),
      "Shift-Ctrl-8": toggleBulletedList,
      "Shift-Ctrl-9": toggleOrderedList,
      Tab: (state, dispatch) => {
        if (sinkListItem(customSchema.nodes.list_item)(state, dispatch)) return true;
        return goToNextCell(1)(state, dispatch);
      },
      "Shift-Tab": (state, dispatch) => {
        if (liftListItem(customSchema.nodes.list_item)(state, dispatch)) return true;
        return goToNextCell(-1)(state, dispatch);
      }
    })
  ]
};

export const serializeContent = (content: string): Node => {
  const domNode = document.createElement("div");
  domNode.innerHTML = content;
  return DOMParser.fromSchema(proseMirrorOptions.schema).parse(domNode);
};

export const deserializeNode = (node: Fragment): string => {
  const div = document.createElement("div");
  const f = DOMSerializer.fromSchema(proseMirrorOptions.schema).serializeFragment(node);
  div.appendChild(f);
  return div.innerHTML;
};

export const htmlToProseMirror = (html: string): Node => {
  const parser = DOMParser.fromSchema(customSchema);
  return parser.parse(document.createRange().createContextualFragment(html));
};

interface ToolbarButtonProps {
  title: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

const ToolbarButton = ({
  title,
  onClick,
  isActive = false,
  disabled = false,
  children
}: ToolbarButtonProps): JSX.Element => (
  <Tooltip title={title} arrow placement="top">
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={onClick}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "6px",
          color: isActive ? "#0f172a" : "#475467",
          backgroundColor: isActive ? "#e2e8f0" : "transparent",
          border: isActive ? "1px solid #cbd5e1" : "1px solid transparent",
          "&:hover": {
            backgroundColor: isActive ? "#cbd5e1" : "#f1f5f9",
            color: "#0f172a"
          },
          "&.Mui-disabled": {
            opacity: 0.4
          }
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const headingOptions = [
  { level: 0, label: "Normal text", desc: "Paragraph" },
  { level: 1, label: "Heading 1", desc: "Title" },
  { level: 2, label: "Heading 2", desc: "Subheading" },
  { level: 3, label: "Heading 3", desc: "Section header" }
];

function getHeadingFontSize(level: number): string {
  if (level === 1) return "1.1rem";
  if (level === 2) return "0.975rem";
  return "0.875rem";
}

interface RichTextfieldProps {
  state: EditorState;
  setState: (state: EditorState) => void;
  // eslint-disable-next-line react/require-default-props
  onChange?: (value?: string) => void;
  // eslint-disable-next-line react/require-default-props
  customKeyWords?: {
    label: string;
    value: string;
  }[];
  minHeight?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  showFontSizeToggleButtons?: boolean;
  editorFontSize?: number;
  showBoldItalicUnderlineOnly?: boolean;
}

export const RichTextfield = ({
  state,
  customKeyWords,
  setState,
  minHeight = 200,
  editorFontSize,
  showBoldItalicUnderlineOnly = false,
  onChange
}: RichTextfieldProps): JSX.Element => {
  // Link popover state
  const [linkAnchorEl, setLinkAnchorEl] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [linkRange, setLinkRange] = useState<{ from: number; to: number } | null>(null);
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);

  // Heading menu state
  const [headingAnchorEl, setHeadingAnchorEl] = useState<HTMLElement | null>(null);

  // Table menu state
  const [tableAnchorEl, setTableAnchorEl] = useState<HTMLElement | null>(null);

  const activeHeadingLevel = getCurrentHeadingLevel(state);
  const activeHeadingLabel = activeHeadingLevel === 0 ? "Normal" : `Heading ${activeHeadingLevel}`;
  const insideTable = isInsideTable(state);

  // Handle Link button click
  const handleLinkButtonClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const activeLink = getActiveLinkInfo(state);
      if (activeLink) {
        setLinkUrl(activeLink.href);
        setLinkText(activeLink.text);
        setLinkRange({ from: activeLink.from, to: activeLink.to });
        setIsEditingExistingLink(true);
      } else {
        const { from, to, empty } = state.selection;
        const selectedText = empty ? "" : state.doc.textBetween(from, to);
        setLinkUrl("");
        setLinkText(selectedText);
        setLinkRange({ from, to });
        setIsEditingExistingLink(false);
      }
      setLinkAnchorEl(e.currentTarget);
    },
    [state]
  );

  const handleLinkClose = () => {
    setLinkAnchorEl(null);
    setLinkUrl("");
    setLinkText("");
    setLinkRange(null);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl.trim()) {
      handleLinkClose();
      return;
    }
    applyLink(
      state,
      (tr) => setState(state.apply(tr)),
      linkUrl,
      linkText || linkUrl,
      linkRange || undefined
    );
    handleLinkClose();
  };

  const handleLinkRemove = () => {
    removeLink(state, (tr) => setState(state.apply(tr)), linkRange || undefined);
    handleLinkClose();
  };

  // Heading menu handlers
  const handleHeadingMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setHeadingAnchorEl(e.currentTarget);
  };

  const handleHeadingMenuClose = () => {
    setHeadingAnchorEl(null);
  };

  const handleHeadingSelect = (level: number) => {
    setHeadingCommand(level)(state, (tr) => setState(state.apply(tr)));
    handleHeadingMenuClose();
  };

  // Table menu handlers
  const handleTableMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setTableAnchorEl(e.currentTarget);
  };

  const handleTableMenuClose = () => {
    setTableAnchorEl(null);
  };

  const handleInsertTableClick = (rows: number, cols: number) => {
    insertTable({
      state,
      dispatch: (tr) => setState(state.apply(tr)),
      rowsCount: rows,
      colsCount: cols,
      withHeaderRow: true,
      cellContent: null
    });
    handleTableMenuClose();
  };

  return (
    <div className={styles.proseMirrorMain}>
      <div className={styles.proseMirrorWrapper}>
        {customKeyWords && customKeyWords.length > 0 && (
          <div className={styles.keywordBar}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, mr: 0.5 }}>
              Insert Keyword:
            </Typography>
            {customKeyWords.map((keyword) => (
              <Box
                key={keyword.label}
                className={styles.keywordSelect}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertCustomKeyword(keyword.value, state, (tr) => setState(state.apply(tr)));
                }}
              >
                {keyword.label}
              </Box>
            ))}
          </div>
        )}

        <div className={styles.proseMirrorToolbar}>
          {/* 1. Bold */}
          <ToolbarButton
            title="Bold (Ctrl+B)"
            isActive={isBold(state)}
            onClick={() => toggleBold(state, (tr) => setState(state.apply(tr)))}
          >
            <FormatBoldIcon fontSize="small" />
          </ToolbarButton>

          {/* 2. Italic */}
          <ToolbarButton
            title="Italic (Ctrl+I)"
            isActive={isItalic(state)}
            onClick={() => toggleItalic(state, (tr) => setState(state.apply(tr)))}
          >
            <FormatItalicIcon fontSize="small" />
          </ToolbarButton>

          {/* 3. Underline */}
          <ToolbarButton
            title="Underline (Ctrl+U)"
            isActive={isUnderline(state)}
            onClick={() => toggleUnderline(state, (tr) => setState(state.apply(tr)))}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolbarButton>

          {!showBoldItalicUnderlineOnly && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, height: 20 }} />

              {/* 4. Link */}
              <ToolbarButton
                title={isLink(state) ? "Edit link" : "Insert link"}
                isActive={isLink(state)}
                onClick={handleLinkButtonClick}
              >
                {isLink(state) ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, height: 20 }} />

              {/* 5. Bullet List */}
              <ToolbarButton
                title="Bullet list"
                isActive={isBulletListActive(state)}
                onClick={() => toggleBulletedList(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatListBulletedIcon fontSize="small" />
              </ToolbarButton>

              {/* 6. Numbered List */}
              <ToolbarButton
                title="Numbered list"
                isActive={isOrderedListActive(state)}
                onClick={() => toggleOrderedList(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatListNumberedIcon fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, height: 20 }} />

              {/* 7. Alignments */}
              <ToolbarButton
                title="Align left"
                isActive={isAlignLeft(state)}
                onClick={() => toggleAlignLeft(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatAlignLeftIcon fontSize="small" />
              </ToolbarButton>

              <ToolbarButton
                title="Align center"
                isActive={isAlignCenter(state)}
                onClick={() => toggleAlignCenter(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatAlignCenterIcon fontSize="small" />
              </ToolbarButton>

              <ToolbarButton
                title="Align right"
                isActive={isAlignRight(state)}
                onClick={() => toggleAlignRight(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatAlignRightIcon fontSize="small" />
              </ToolbarButton>

              <ToolbarButton
                title="Justify"
                isActive={isAlignJustify(state)}
                onClick={() => toggleAlignJustify(state, (tr) => setState(state.apply(tr)))}
              >
                <FormatAlignJustifyIcon fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, height: 20 }} />

              {/* 8. Headings */}
              <Tooltip title="Heading style" arrow placement="top">
                <MuiButton
                  size="small"
                  variant="text"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleHeadingMenuOpen}
                  endIcon={<ArrowDropDownIcon fontSize="small" />}
                  startIcon={<TitleIcon fontSize="small" />}
                  sx={{
                    height: 32,
                    px: 1,
                    minWidth: 105,
                    justifyContent: "space-between",
                    textTransform: "none",
                    color: activeHeadingLevel > 0 ? "#0f172a" : "#475467",
                    backgroundColor: activeHeadingLevel > 0 ? "#e2e8f0" : "transparent",
                    border: activeHeadingLevel > 0 ? "1px solid #cbd5e1" : "1px solid transparent",
                    borderRadius: "6px",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: activeHeadingLevel > 0 ? "#cbd5e1" : "#f1f5f9",
                      color: "#0f172a"
                    }
                  }}
                >
                  {activeHeadingLabel}
                </MuiButton>
              </Tooltip>

              <Menu
                anchorEl={headingAnchorEl}
                open={Boolean(headingAnchorEl)}
                onClose={handleHeadingMenuClose}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
              >
                {headingOptions.map((opt) => (
                  <MenuItem
                    key={opt.level}
                    selected={activeHeadingLevel === opt.level}
                    onClick={() => handleHeadingSelect(opt.level)}
                    sx={{ minWidth: 160 }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: opt.level > 0 ? 600 : 400,
                            fontSize: getHeadingFontSize(opt.level)
                          }}
                        >
                          {opt.label}
                        </Typography>
                      }
                      secondary={opt.desc}
                    />
                  </MenuItem>
                ))}
              </Menu>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, height: 20 }} />

              {/* 9. Table */}
              <Tooltip title="Table" arrow placement="top">
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleTableMenuOpen}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "6px",
                    color: insideTable ? "#0f172a" : "#475467",
                    backgroundColor: insideTable ? "#e2e8f0" : "transparent",
                    border: insideTable ? "1px solid #cbd5e1" : "1px solid transparent",
                    "&:hover": {
                      backgroundColor: insideTable ? "#cbd5e1" : "#f1f5f9",
                      color: "#0f172a"
                    }
                  }}
                >
                  <TableChartOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={tableAnchorEl}
                open={Boolean(tableAnchorEl)}
                onClose={handleTableMenuClose}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
              >
                {insideTable && [
                  <MenuItem
                    key="add-p-above-table"
                    onClick={() => {
                      handleTableMenuClose();
                      insertParagraphBeforeTable(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <PostAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Paragraph Above Table" />
                  </MenuItem>,
                  <MenuItem
                    key="add-p-below-table"
                    onClick={() => {
                      handleTableMenuClose();
                      insertParagraphAfterTable(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <PostAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Paragraph Below Table" />
                  </MenuItem>,
                  <Divider key="div-p-below" />,
                  <MenuItem
                    key="add-row-before"
                    onClick={() => {
                      handleTableMenuClose();
                      addRowBefore(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <TableRowsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Row Above" />
                  </MenuItem>,
                  <MenuItem
                    key="add-row-after"
                    onClick={() => {
                      handleTableMenuClose();
                      addRowAfter(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <TableRowsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Row Below" />
                  </MenuItem>,
                  <MenuItem
                    key="delete-row"
                    onClick={() => {
                      handleTableMenuClose();
                      deleteRow(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Row" />
                  </MenuItem>,
                  <Divider key="div-1" />,
                  <MenuItem
                    key="add-col-before"
                    onClick={() => {
                      handleTableMenuClose();
                      addColumnBefore(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <ViewColumnIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Column Left" />
                  </MenuItem>,
                  <MenuItem
                    key="add-col-after"
                    onClick={() => {
                      handleTableMenuClose();
                      addColumnAfter(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <ViewColumnIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Column Right" />
                  </MenuItem>,
                  <MenuItem
                    key="delete-col"
                    onClick={() => {
                      handleTableMenuClose();
                      deleteColumn(state, (tr) => setState(state.apply(tr)));
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Column" />
                  </MenuItem>,
                  <Divider key="div-2" />,
                  <MenuItem
                    key="delete-table"
                    onClick={() => {
                      handleTableMenuClose();
                      deleteTable(state, (tr) => setState(state.apply(tr)));
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <ListItemIcon sx={{ color: "error.main" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Table" />
                  </MenuItem>,
                  <Divider key="div-3" />
                ]}
                <MenuItem onClick={() => handleInsertTableClick(2, 2)}>
                  <ListItemIcon>
                    <TableChartOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Insert 2 × 2 Table" />
                </MenuItem>
                <MenuItem onClick={() => handleInsertTableClick(3, 3)}>
                  <ListItemIcon>
                    <TableChartOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Insert 3 × 3 Table" />
                </MenuItem>
                <MenuItem onClick={() => handleInsertTableClick(4, 4)}>
                  <ListItemIcon>
                    <TableChartOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Insert 4 × 4 Table" />
                </MenuItem>
              </Menu>
            </>
          )}
        </div>

        {/* Link Editing / Inserting Popover */}
        <Popover
          open={Boolean(linkAnchorEl)}
          anchorEl={linkAnchorEl}
          onClose={handleLinkClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 2,
              width: 320,
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
              borderRadius: "8px"
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {isEditingExistingLink ? "Edit Link" : "Insert Link"}
          </Typography>
          <Stack spacing={1.5}>
            {(!linkRange || linkRange.from === linkRange.to) && !isEditingExistingLink && (
              <TextField
                label="Text"
                size="small"
                fullWidth
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Display text"
              />
            )}
            <TextField
              label="URL"
              size="small"
              fullWidth
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleLinkSubmit();
                } else if (e.key === "Escape") {
                  handleLinkClose();
                }
              }}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" pt={0.5}>
              {isEditingExistingLink ? (
                <MuiButton
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={handleLinkRemove}
                  sx={{ textTransform: "none" }}
                >
                  Remove
                </MuiButton>
              ) : (
                <div />
              )}
              <Box display="flex" gap={1}>
                <MuiButton
                  size="small"
                  variant="text"
                  onClick={handleLinkClose}
                  sx={{ textTransform: "none", color: "#64748b" }}
                >
                  Cancel
                </MuiButton>
                <MuiButton
                  size="small"
                  variant="contained"
                  onClick={handleLinkSubmit}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#334155",
                    "&:hover": { backgroundColor: "#1e293b" }
                  }}
                  disabled={!linkUrl.trim()}
                >
                  {isEditingExistingLink ? "Save" : "Apply"}
                </MuiButton>
              </Box>
            </Box>
          </Stack>
        </Popover>

        {/* ProseMirror Content Area */}
        <div className={styles.proseMirrorContainer}>
          <ProseMirror
            className={styles.proseMirror}
            style={{ minHeight, ...(editorFontSize ? { fontSize: editorFontSize } : {}) }}
            state={state}
            onChange={(e) => {
              setState(e);
              onChange?.();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RichTextfield;
